import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { WarningBadge } from "@/components/Badges";
import ClickableImage from "@/components/ClickableImage";
import { ArrowLeft, User, Phone, CreditCard, Car, MapPin, FileText, History, Image as ImageIcon, AlertTriangle, Calendar, PencilLine } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

function Field({ icon: Icon, label, value }) {
  return (
    <div className="flex gap-3">
      <div className="p-2 bg-white/5 rounded-sm text-zinc-400 h-fit"><Icon className="w-4 h-4" /></div>
      <div>
        <div className="text-xs uppercase tracking-wider text-zinc-500">{label}</div>
        <div className="text-white font-medium mt-0.5 break-words">{value || "—"}</div>
      </div>
    </div>
  );
}

function Img({ path, label }) {
  if (!path) return null;
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">{label}</div>
      <ClickableImage path={path} label={label} className="w-full aspect-video object-cover border border-zinc-800 rounded-sm hover:border-primary transition-colors" />
    </div>
  );
}

export default function BlacklistDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [r, setR] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/blacklist/${id}`).then((res) => setR(res.data)).catch(() => setErr(true));
  }, [id]);

  if (err) return <p className="text-zinc-500">Không tìm thấy báo cáo.</p>;
  if (!r) return <p className="text-zinc-600">Đang tải...</p>;

  const hasImages = r.cccd_front || r.cccd_back || r.license_image || (r.related_images || []).length;

  return (
    <div className="max-w-4xl">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-zinc-500 hover:text-white text-sm mb-6 transition-colors">
        <ArrowLeft className="w-4 h-4" /> Quay lại
      </button>

      <div className="flex flex-wrap items-center gap-4 mb-6">
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-white">{r.customer_name}</h1>
        <WarningBadge level={r.warning_level} />
        {user?.role === "root" && (
          <button
            data-testid="edit-report-btn"
            onClick={() => navigate(`/sua-danh-sach/${r.id}`)}
            className="ml-auto flex items-center gap-2 border border-zinc-700 text-zinc-300 hover:text-white hover:border-primary rounded-sm px-4 py-2 text-sm transition-colors"
          >
            <PencilLine className="w-4 h-4" /> Chỉnh sửa
          </button>
        )}
      </div>

      {r.report_count > 1 && (
        <div data-testid="repeat-warning" className="mb-6 flex items-center gap-3 bg-red-500/15 border border-red-500 rounded-sm p-4 shadow-[0_0_20px_rgba(230,0,0,0.25)]">
          <AlertTriangle className="w-6 h-6 text-red-400 shrink-0 animate-pulse" />
          <div>
            <div className="font-display font-black text-red-400 uppercase tracking-wide text-sm">
              Cảnh báo trùng khớp
            </div>
            <div className="text-red-200 text-sm">
              Khách hàng này đã bị báo cáo <span className="font-black">{r.report_count} lần</span>. Hãy đặc biệt thận trọng!
            </div>
          </div>
        </div>
      )}

      <div className="bg-card border border-zinc-800 rounded-sm p-6 grid sm:grid-cols-2 gap-5 mb-6">
        <Field icon={User} label="Tên khách hàng" value={r.customer_name} />
        <Field icon={Calendar} label="Ngày tháng năm sinh" value={r.dob} />
        <Field icon={Phone} label="Số điện thoại" value={r.phone} />
        <Field icon={CreditCard} label="Số CCCD/CMND" value={r.cccd_number} />
        <Field icon={Car} label="Số bằng lái" value={r.license_number} />
        <Field icon={MapPin} label="Địa chỉ" value={r.address} />
        <Field icon={User} label="Nhà xe báo cáo" value={r.reported_by_name} />
        <div className="sm:col-span-2">
          <Field icon={FileText} label="Lý do báo cáo" value={r.reason} />
        </div>
        <Field icon={History} label="Ngày báo cáo" value={new Date(r.report_date).toLocaleString("vi-VN")} />
      </div>

      {hasImages ? (
        <div className="bg-card border border-zinc-800 rounded-sm p-6 mb-6">
          <h2 className="font-display text-lg font-bold text-white flex items-center gap-2 mb-4">
            <ImageIcon className="w-5 h-5 text-primary" /> Hình ảnh & tài liệu
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Img path={r.cccd_front} label="CCCD mặt trước" />
            <Img path={r.cccd_back} label="CCCD mặt sau" />
            <Img path={r.license_image} label="Bằng lái" />
            {(r.related_images || []).map((p, i) => (
              <Img key={i} path={p} label={`Tài liệu liên quan ${i + 1}`} />
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-card border border-zinc-800 rounded-sm p-6 mb-6">
          <h2 className="font-display text-lg font-bold text-white flex items-center gap-2 mb-3">
            <ImageIcon className="w-5 h-5 text-primary" /> Hình ảnh & tài liệu
          </h2>
          <p data-testid="no-images" className="text-zinc-600 text-sm">Báo cáo này chưa đính kèm hình ảnh hoặc bằng chứng.</p>
        </div>
      )}

      <div className="bg-card border border-zinc-800 rounded-sm p-6">
        <h2 className="font-display text-lg font-bold text-white flex items-center gap-2 mb-4">
          <History className="w-5 h-5 text-primary" /> Lịch sử vi phạm
        </h2>
        {r.history?.length ? (
          <div className="relative border-l border-zinc-800 pl-6 space-y-6">
            {r.history.map((h) => (
              <div key={h.id} data-testid={`history-${h.id}`} className="relative">
                <div className="absolute -left-[27px] top-1 w-3 h-3 rounded-full bg-primary" />
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <WarningBadge level={h.warning_level} />
                  <span className="text-xs text-zinc-500">{new Date(h.report_date).toLocaleDateString("vi-VN")} · {h.reported_by_name}</span>
                </div>
                <p className="text-zinc-300 text-sm">{h.reason}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-zinc-600 text-sm">Không có vi phạm nào khác được ghi nhận cho khách hàng này.</p>
        )}
      </div>
    </div>
  );
}
